import { useCallback, useEffect, useMemo, useState } from 'react';

function initialState(schoolId) {
  return { schoolId, domains: [], loaded: !schoolId, loading: Boolean(schoolId), error: null };
}

/** One registry lifecycle for web/native; a failed read is never an empty school. */
export function useDomainRegistry(schoolId, subscribe) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(() => initialState(schoolId));
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    let current = true;
    let unsubscribe;
    setState(previous => ({
      ...(previous.schoolId === schoolId ? previous : initialState(schoolId)),
      loading: Boolean(schoolId), error: null,
    }));
    const receive = (domains, error = null) => {
      if (!current) return;
      setState(previous => {
        const scoped = previous.schoolId === schoolId ? previous : initialState(schoolId);
        // Web isolates malformed definitions and supplies an issues list;
        // native/older subscribers report read failures in this same position.
        const issues = Array.isArray(error) ? error : [];
        if (error && !Array.isArray(error)) return {
          ...scoped, loading: false,
          error: scoped.loaded
            ? 'Domains could not be refreshed. Showing the last loaded setup.'
            : 'Domains could not be loaded. Try again.',
        };
        return { schoolId, domains, loaded: true, loading: false,
          error: issues.length
            ? `${issues.length} ${issues.length === 1 ? 'domain could' : 'domains could'} not load.`
            : null,
        };
      });
    };
    if (!schoolId) receive([]);
    else {
      try { unsubscribe = subscribe(schoolId, receive, error => receive(null, error)); }
      catch (error) { receive(null, error); }
    }
    return () => { current = false; unsubscribe?.(); };
  }, [schoolId, subscribe, attempt]);

  // Never expose the previous school's definitions, even before effects run.
  return useMemo(() => ({
    ...(state.schoolId === schoolId ? state : initialState(schoolId)), retry,
  }), [state, schoolId, retry]);
}
